import type { JSX, SVGProps } from 'react';

/**
 * Значки интерфейса.
 *
 * Здесь они рисованные, а не эмодзи, и это решение стоит объяснить.
 *
 * Эмодзи бесплатны в разработке и дороги на экране. Во-первых, они цветные:
 * пять разноцветных картинок в нижней панели перетягивают на себя внимание
 * с того, ради чего человек открыл приложение, — со списка подарков. Во-вторых,
 * рисует их не приложение, а система: на iPhone одни, на Android другие,
 * в Windows третьи, и облик панели меняется от устройства к устройству.
 * В-третьих, у эмодзи нет состояний — активный пункт нельзя показать линией
 * потолще или заливкой, только сдвигом и увеличением, что и приходилось делать.
 *
 * Линейные значки решают всё три сразу: цвет берётся от текста (`currentColor`),
 * поэтому активный пункт красится тем же правилом, что и подпись; облик один
 * на всех устройствах; вес линии наш.
 *
 * Размер по умолчанию 22 — при подписи в 0,66rem это соотношение читается
 * как «значок с подписью», а не «картинка и текст рядом».
 */

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 22, children, ...props }: IconProps & { children: JSX.Element }): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

/** Мои списки: страница с пунктами. */
export function IconLists(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M8 4h9a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
        <path d="M9.5 9.5h6M9.5 13h6M9.5 16.5h3.5" />
      </g>
    </Icon>
  );
}

/** Друзьям: подарок. */
export function IconGift(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M4 11h16v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8Z" />
        <path d="M3.5 7.5h17v3.5h-17z" />
        <path d="M12 7.5V21" />
        <path d="M12 7.5S11 3.5 8.75 3.5a2 2 0 0 0 0 4H12Zm0 0s1-4 3.25-4a2 2 0 0 1 0 4H12Z" />
      </g>
    </Icon>
  );
}

/**
 * Я дарю: сердце в раскрытой ладони.
 *
 * Ладонь именно чашей, а не прямой чертой. С чертой значок читался как
 * «сердце на полке»: линия под фигурой на таком размере не связывается с ней
 * в один предмет, а выглядит подчёркиванием. Дуга, загнутая вверх по краям,
 * сразу читается как то, на чём сердце лежит.
 */
export function IconGiving(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M12 8.9 10.95 7.9a2.3 2.3 0 0 0-3.25 3.25L12 15.4l4.3-4.25A2.3 2.3 0 0 0 13.05 7.9L12 8.9Z" />
        <path d="M4.2 17.2c0 2.4 1.9 4.1 4.3 4.1h7c2.4 0 4.3-1.7 4.3-4.1" />
      </g>
    </Icon>
  );
}

/** Друзья: двое. */
export function IconFriends(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <circle cx="9.5" cy="8" r="3.2" />
        <path d="M3.5 19.5c0-3 2.7-5 6-5s6 2 6 5" />
        <path d="M16.2 5.3a3.2 3.2 0 0 1 0 6.1" />
        <path d="M18 14.9c1.6.7 2.6 2.1 2.6 4.6" />
      </g>
    </Icon>
  );
}

/** Профиль: человек в круге. */
export function IconProfile(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="10" r="2.8" />
        <path d="M6.6 18.6c1-2.1 3-3.2 5.4-3.2s4.4 1.1 5.4 3.2" />
      </g>
    </Icon>
  );
}

/* ------------------------------------------------------------------ */
/* Значки действий в карточке                                          */
/* ------------------------------------------------------------------ */

/** Поднять выше. */
export function IconUp(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M12 19V5" />
        <path d="m6.5 10.5 5.5-5.5 5.5 5.5" />
      </g>
    </Icon>
  );
}

/** Опустить ниже. */
export function IconDown(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M12 5v14" />
        <path d="m6.5 13.5 5.5 5.5 5.5-5.5" />
      </g>
    </Icon>
  );
}

/** Изменить: карандаш. */
export function IconEdit(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M4 20h4.2L19 9.2a2.1 2.1 0 0 0-3-3L5.2 17 4 20Z" />
        <path d="m14.8 7.4 2.8 2.8" />
      </g>
    </Icon>
  );
}

/** Удалить: корзина. */
export function IconTrash(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M4.5 7h15" />
        <path d="M9.5 7V5.4A1.4 1.4 0 0 1 10.9 4h2.2a1.4 1.4 0 0 1 1.4 1.4V7" />
        <path d="M6.5 7.5 7.3 19a1.9 1.9 0 0 0 1.9 1.7h5.6a1.9 1.9 0 0 0 1.9-1.7l.8-11.5" />
      </g>
    </Icon>
  );
}

/* ------------------------------------------------------------------ */
/* Значки пустых состояний                                             */
/* ------------------------------------------------------------------ */

/** Ничего не нашлось. */
export function IconSearch(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <circle cx="10.8" cy="10.8" r="6.3" />
        <path d="m15.4 15.4 4.6 4.6" />
      </g>
    </Icon>
  );
}

/** Закрыто: замок. */
export function IconLock(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <rect x="4.8" y="10.3" width="14.4" height="10" rx="2.2" />
        <path d="M8.3 10.3V7.9a3.7 3.7 0 0 1 7.4 0v2.4" />
      </g>
    </Icon>
  );
}

/** Ссылка. */
export function IconLink(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M10.2 13.8a3.6 3.6 0 0 0 5.1 0l3-3a3.6 3.6 0 0 0-5.1-5.1l-1.4 1.4" />
        <path d="M13.8 10.2a3.6 3.6 0 0 0-5.1 0l-3 3a3.6 3.6 0 0 0 5.1 5.1l1.4-1.4" />
      </g>
    </Icon>
  );
}

/** Пусто: раскрытая коробка. */
export function IconEmptyBox(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M4 10.5 12 6l8 4.5-8 4.5-8-4.5Z" />
        <path d="M4 10.5v6.2l8 4.3 8-4.3v-6.2" />
      </g>
    </Icon>
  );
}

/** Начало: искра. Тот же знак, что и на значке приложения. */
export function IconSparkle(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M12 3.4c0 4.2 1.9 6.1 6.1 6.1-4.2 0-6.1 1.9-6.1 6.1 0-4.2-1.9-6.1-6.1-6.1 4.2 0 6.1-1.9 6.1-6.1Z" />
        <path d="M17.9 15.4c0 2.1 1 3.1 3.1 3.1-2.1 0-3.1 1-3.1 3.1 0-2.1-1-3.1-3.1-3.1 2.1 0 3.1-1 3.1-3.1Z" />
      </g>
    </Icon>
  );
}

/**
 * Пустое место вместо фотографии.
 *
 * Тот же подарок, что и в нижней панели, но крупнее и линией потоньше: он
 * должен читаться как рамка для будущей картинки, а не как содержимое.
 */
export function IconGiftOutline(props: IconProps): JSX.Element {
  return <IconGift strokeWidth={1.2} {...props} />;
}

/*
 * Значки для полос-сообщений (`Notice`).
 *
 * До этого в полосах стояли эмодзи: ⚠️ для беды, ✓ для удачи, 🤫 и 🙈 для
 * тайны, ⏳ и 📱 для ожидания и телефона. Двадцать восемь штук на весь
 * интерфейс — и каждая рисуется системой, то есть на iPhone, Android и в
 * Windows продукт выглядит по-разному в местах, где он объясняется словами.
 * Эмодзи остаются там, где их выбирает человек: обложка списка, подписи к
 * желаниям. Служебные знаки — рисованные, одной линией с остальными.
 */

/** Беда или предупреждение. */
export function IconWarning(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M12 4.6 3.4 19.4h17.2L12 4.6Z" />
        <path d="M12 10v4" />
        <path d="M12 17.1h.01" />
      </g>
    </Icon>
  );
}

/** Удача: сделано. */
export function IconCheck(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <path d="M4.5 12.5 9.5 17.5 19.5 7" />
    </Icon>
  );
}

/** Пояснение: просто к сведению. */
export function IconInfo(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <circle cx="12" cy="12" r="8.4" />
        <path d="M12 11v5.2" />
        <path d="M12 7.9h.01" />
      </g>
    </Icon>
  );
}

/**
 * Тайна: перечёркнутый глаз.
 *
 * Главное обещание продукта — владелец не видит броней, — и в интерфейсе оно
 * появляется чаще любого другого знака. Эмодзи 🙈 и 🤫 обозначали его двумя
 * разными картинками; здесь он один.
 */
export function IconEyeOff(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M3.5 12s3.4-6 8.5-6c1.4 0 2.7.45 3.8 1.1" />
        <path d="M19.2 9.1c.7.9 1.3 1.9 1.3 2.9 0 0-3.4 6-8.5 6-1.1 0-2.1-.28-3-.72" />
        <path d="m9.9 9.9a3 3 0 0 0 4.2 4.2" />
        <path d="M4 4l16 16" />
      </g>
    </Icon>
  );
}

/** Ожидание. */
export function IconClock(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <circle cx="12" cy="12" r="8.4" />
        <path d="M12 7.4V12l3 1.8" />
      </g>
    </Icon>
  );
}

/** Телефон: про установку на домашний экран. */
export function IconPhone(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <rect x="6.6" y="2.8" width="10.8" height="18.4" rx="2.4" />
        <path d="M10.8 5.6h2.4" />
        <path d="M12 18.1h.01" />
      </g>
    </Icon>
  );
}

/** Письмо: сказали «проверьте почту». */
export function IconMail(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <rect x="3.2" y="5.4" width="17.6" height="13.2" rx="2.4" />
        <path d="m3.8 7 7.1 5.2a2 2 0 0 0 2.2 0L20.2 7" />
      </g>
    </Icon>
  );
}

/** Повтор: такая ссылка в списке уже есть. */
export function IconRepeat(props: IconProps): JSX.Element {
  return (
    <Icon {...props}>
      <g>
        <path d="M4.2 10.4V9a3 3 0 0 1 3-3h9.4" />
        <path d="m14.2 3.6 2.6 2.4-2.6 2.4" />
        <path d="M19.8 13.6V15a3 3 0 0 1-3 3H7.4" />
        <path d="m9.8 20.4-2.6-2.4 2.6-2.4" />
      </g>
    </Icon>
  );
}
